import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { LoadingSkeleton, ModalComponent, Switch, TableListV2, Tag, useQueryFetch } from 'sonata-react-components';
import { getCampaignPlanAdsStatus, getInsertionOrderAdsStatus } from 'sonata-static-storage/lib/entities';
import styled from 'styled-components';

import { useModals } from '@/router';
import { useGlobalStore } from '@/store/global';
import { getSessionByKeys } from '@/store/global/session/selectors';

import type { MultiflightAdsStatus } from 'sonata-static-storage/lib/common/common.types';

const tagConfig = {
  approved: {
    icon: {
      position: 'left',
      webFont: 'fa-regular fa-circle-check'
    },
    status: 'ok'
  },
  denied: {
    icon: {
      position: 'left',
      webFont: 'fa-regular fa-circle-xmark'
    },
    status: 'wrong'
  },
  pending: {
    icon: {
      position: 'left',
      webFont: 'fa-regular fa-clock'
    },
    status: 'warning'
  },
  unknown: {
    icon: {
      position: 'left',
      webFont: 'fa-regular fa-circle-question'
    },
    status: 'warning'
  }
} as const;

const SkeletonStyled = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  width: 100%;

  .adsValidationSkeleton {
    &__row {
      align-items: center;
      border-radius: 4px;
      box-sizing: border-box;
      display: grid;
      grid-auto-flow: column;
      grid-column-gap: 8px;
      grid-template-columns: auto auto 1fr;
      grid-template-rows: auto;
      height: 50px;
      justify-items: end;
      outline: ${props => props.theme.uiColors.tableBorderRow} solid 1px;
      padding: 8px 16px;
    }
  }
`;

const Skeleton = () => {
  return (
    <SkeletonStyled className="adsValidationSkeleton">
      {Array.from({ length: 5 }).map((_, keyIndex) => (
        <div className="adsValidationSkeleton__row" key={keyIndex}>
          <LoadingSkeleton height={16} width={16} />
          <LoadingSkeleton height={20} width={82} />
          <LoadingSkeleton height={20} width={37} />
        </div>
      ))}
    </SkeletonStyled>
  );
};

export default function AdsValidation() {
  const { formatMessage } = useIntl();
  const { state } = useLocation();
  const modals = useModals();
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));

  const { data = [], isLoading } = useQueryFetch(
    ['get_data', state.origin, state.value._id],
    async () => {
      if (state.origin === 'campaignPlan') {
        return getCampaignPlanAdsStatus(state.value._id, userId);
      }
      return getInsertionOrderAdsStatus(state.value._id, userId);
    },
    {
      enabled: true
    }
  );

  const handleClose = () =>
    modals.close({
      at: {
        pathname: '/campaigns',
        search: location.search
      }
    });

  const getStandardData = (tableData: MultiflightAdsStatus) => {
    return tableData?.map(t => ({
      additionalContentLeft: [],
      additionalContentRight: [
        t.status.map(s => (
          <Tag
            icon={{
              position: 'left',
              webFont: 'fa-regular fa-circle-check'
            }}
            id={`tag-${s.id}`}
            key={s.id}
          >
            {s.value}
          </Tag>
        ))
      ],
      expandableContent: () => (
        <TableListV2
          columns={[]}
          config={{
            maxHeight: 556,
            selectableItem: false,
            type: 'list'
          }}
          isCollapsable
          isCompactList={false}
          isSelectableList={false}
          showColumnsHeader={false}
          standardData={t.items?.map(i => ({
            additionalContentLeft: [],
            additionalContentRight: [
              i.status.map(itemStatus => {
                const config = tagConfig[itemStatus.id];
                return (
                  <Tag id={`tag-${itemStatus.id}`} key={itemStatus.id} {...config}>
                    {itemStatus.value}
                  </Tag>
                );
              })
            ],
            isSelectableList: false,
            title: i.name
          }))}
        />
      ),
      isSelectableList: false,
      title: t.name
    }));
  };

  return (
    <ModalComponent
      isModalOpened
      onClose={handleClose}
      portalId="ads_validation"
      theme="light"
      title={formatMessage({
        defaultMessage: 'Ads Validation',
        id: 'adsValidation.title'
      })}
      width="805px"
    >
      <ModalComponent.ModalContent>
        <Switch>
          <Switch.Case condition={isLoading}>
            <Skeleton />
          </Switch.Case>
          <Switch.Default>
            <TableListV2
              columns={[]}
              config={{
                emptyState: {
                  icon: 'fal fa-empty-set',
                  show: data.length === 0,
                  title: formatMessage({
                    defaultMessage: 'There are no ads to validate.',
                    id: 'adsValidation.emptyState'
                  })
                },
                maxHeight: 556,
                selectableItem: false,
                type: 'list'
              }}
              isCollapsable
              isCompactList={false}
              isSelectableList={false}
              showColumnsHeader={false}
              standardData={getStandardData(data)}
            />
          </Switch.Default>
        </Switch>
      </ModalComponent.ModalContent>
    </ModalComponent>
  );
}
